// Master Apps · Catálogo (colaborador autenticado) — porte fiel de catalog.jsx.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppScreenshot, VisibilityBadge } from '@/components/app-visuals';
import {
  IconAlert,
  IconExternal,
  IconFilter,
  IconRefresh,
  IconSearch,
  IconX,
} from '@/components/icons';
import { ButtonGhost, FilterChip, Sidebar, TopBar } from '@/components/primitives';
import { useToggleFavorite } from '@/hooks/use-favorites';
import { costsByRepoSlug, repoSlug, useRailwayCosts } from '@/hooks/use-railway-costs';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import { APP_TYPES, type App } from '@/lib/catalog-config';
import { formatDateShortBR, formatUsdBR, normalizeSearchText } from '@/lib/format';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';
import { ADMIN_ONLY_VIEWS, type CatalogView, useCatalogPrefs } from '@/store/catalog-prefs';

/**
 * URL que o card deve abrir no ambiente escolhido. Em homologação usa a
 * `urlHml`; só cai na de produção quando a aplicação não tem homologação
 * cadastrada — e nesse caso o card avisa ("sem ambiente de homologação"), para
 * ninguém achar que está testando quando está no sistema real.
 *
 * `null` quando não há NADA para abrir: a URL de produção virou nullable
 * (obrigatória só quando a situação é produção), então uma app em dev pode só
 * ter a `urlDev` — último fallback — ou nenhuma. Quem chama decide não abrir.
 */
export function resolveAppUrl(app: App, env: 'prod' | 'homolog' = 'prod'): string | null {
  if (env === 'homolog' && app.urlHml) return app.urlHml;
  return app.url ?? app.urlHml ?? app.urlDev;
}

/**
 * Colunas da grade de cards do catálogo (blocos "Favoritos" e "Todas as
 * aplicações").
 *
 * Três colunas só cabem acima de 1024px. Medido a 375px com as três: o card
 * ficava com 98px de largura, o nome virava "Ma…" e o rodapé espremia
 * "PRODUÇÃO" contra "atual. 11/08" (as duas metades se sobrepunham). Uma coluna
 * no celular, duas no tablet.
 *
 * `minmax(0, 1fr)` nas faixas novas e não `1fr`: `1fr` tem mínimo automático
 * igual ao conteúdo, então uma descrição longa empurraria a coluna além da
 * faixa e devolveria a rolagem lateral que estamos tirando. O desktop segue com
 * o `repeat(3, 1fr)` literal de sempre.
 */
export function appGridColumns(vp: Pick<Viewport, 'bp'>): string {
  return byViewport(vp, {
    mobile: 'minmax(0, 1fr)',
    tablet: 'repeat(2, minmax(0, 1fr))',
    desktop: 'repeat(3, 1fr)',
  });
}

const CRITICALITY_LABELS: Record<string, string> = {
  alta: 'Crítica',
  media: 'Média',
  baixa: 'Baixa',
};

// A aba "Produção" lista o que está no ar: em produção, em manutenção ou
// depreciado (continua acessível). A aba "Homologação" lista o que tem URL de
// homologação cadastrada OU está declarado nesse status.
//
// `pausado` fica de FORA da aba de produção, ao contrário de `depreciado`: o
// depreciado continua respondendo (por isso o colaborador acha o link ali), e o
// pausado é projeto que parou antes de virar rotina de alguém. Listá-lo em
// "Produção" prometeria um sistema que não atende.
const isOnProdTab = (a: App) =>
  a.status === 'prod' || a.status === 'manutencao' || a.status === 'depreciado';
const isOnHomologTab = (a: App) => !!a.urlHml || a.status === 'homolog';

// Visão do usuário final: tudo é "aplicação".
// NÃO entram: internas (só admin), RPA e Automação/Integração (só administração).
const ADMIN_ONLY_TYPES = new Set(['rpa', 'integracao', 'data', 'infra']);

/**
 * Base da listagem por aba. "Todos" é literal: tudo que o usuário pode ver,
 * em qualquer status. As demais abas mantêm as regras de Produção/Homologação
 * e os status individuais do admin.
 */
function appsForView(apps: App[], view: CatalogView): App[] {
  if (view === 'todos') return apps;
  if (view === 'prod') return apps.filter(isOnProdTab);
  if (view === 'homolog') return apps.filter(isOnHomologTab);
  return apps.filter((a) => a.status === view);
}

// "agora mesmo" · "há 3 min" · "há 2 h" — a partir do carimbo real do último
// fetch. Sem carimbo a tela não mostra nada (é melhor do que anunciar uma
// atualização que não aconteceu).
function formatFreshness(at: number | Date): string {
  const minutes = Math.floor((Date.now() - (at instanceof Date ? at.getTime() : at)) / 60000);
  if (minutes < 1) return 'agora mesmo';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

function CatalogTopbar({
  dark,
  accent,
  query,
  setQuery,
  filtersOpen,
  onToggleFilters,
  activeFilters,
}: Readonly<{
  dark: boolean;
  accent: string;
  query: string;
  setQuery: (v: string) => void;
  filtersOpen: boolean;
  onToggleFilters: () => void;
  activeFilters: number;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const searchRef = useRef<HTMLInputElement | null>(null);

  // O atalho anunciado no campo precisa existir de verdade.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    // O hambúrguer de navegação NÃO é montado aqui: esta barra é o <TopBar/>
    // compartilhado com um `right` próprio, e ele já renderiza o <NavToggle/>
    // como primeiro filho (conferido no navegador a 375px: existe um botão
    // "Abrir navegação"). Repetir o toggle daria dois hambúrgueres.
    <TopBar
      dark={dark}
      title="Catálogo"
      subtitle="Aplicações da Masterboi disponíveis para você"
      right={
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              // 42 e não 40 no celular: a caixa é `border-box` com 1px de borda,
              // então o <input> esticado dentro dela herda 40 líquidos — que é o
              // alvo de toque que interessa (com 40 na caixa media 38).
              height: byViewport(vp, { mobile: 42, tablet: 34, desktop: 34 }),
              padding: '0 12px',
              background: p.surface2,
              border: `1px solid ${p.border}`,
              borderRadius: 7,
              // 280px fixos + os 97px do botão "Filtros" não cabem em 347px de
              // linha útil (375 menos o padding da barra): o campo passa a
              // dividir a linha com o botão em vez de empurrá-lo para baixo.
              // `<string | number>` explícito: o valor do celular é palavra-chave
              // ('auto') e o das outras faixas é número, então a inferência não
              // tem um tipo só para escolher.
              width: byViewport<string | number>(vp, { mobile: 'auto', tablet: 280, desktop: 280 }),
              flex: byViewport(vp, { mobile: '1 1 150px', tablet: undefined, desktop: undefined }),
            }}
          >
            <span style={{ color: p.muted, display: 'flex' }}>
              <IconSearch size={14} />
            </span>
            <input
              ref={searchRef}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              // Texto do placeholder é o mesmo em toda faixa: o <input> corta o
              // excedente sozinho, e trocar a frase por faixa daria duas cópias
              // da mesma mensagem para manter.
              placeholder="Buscar app, time, responsável…"
              style={{
                flex: 1,
                // O <input> media 180x18 (medido): centrado pelo flex, a área
                // clicável era a altura do texto, não a da caixa. Esticado, o
                // toque em qualquer ponto da caixa cai no campo.
                alignSelf: byViewport(vp, {
                  mobile: 'stretch',
                  tablet: undefined,
                  desktop: undefined,
                }),
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: 12,
                color: p.text,
                fontFamily: fontSans,
              }}
            />
            {/* Celular não tem Ctrl: o selo do atalho só ocuparia ~40px do
                campo de busca anunciando uma tecla que não existe ali. */}
            {!vp.mobile && (
              <span
                style={{
                  fontSize: 9,
                  color: p.muted,
                  fontFamily: fontMono,
                  padding: '2px 5px',
                  borderRadius: 3,
                  border: `1px solid ${p.border}`,
                }}
              >
                Ctrl+K
              </span>
            )}
          </div>
          <ButtonGhost dark={dark} onClick={onToggleFilters}>
            <IconFilter size={13} /> Filtros
            {activeFilters > 0 ? (
              <span
                style={{
                  ...numeroTabular,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: accent,
                  color: '#fff',
                }}
              >
                {activeFilters}
              </span>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  color: p.muted,
                  display: 'inline-flex',
                  transform: filtersOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 140ms',
                }}
              >
                ▾
              </span>
            )}
          </ButtonGhost>
        </>
      }
    />
  );
}

// Controle segmentado · Todos / Produção / Homologação (+ status de admin)
function EnvSegmented({
  sel,
  setEnv,
  counts,
  dark,
  showAllStatuses = false,
}: Readonly<{
  sel: string;
  setEnv?: (id: string) => void;
  counts: {
    todos: number;
    prod: number;
    homolog: number;
    dev: number;
    manutencao: number;
    pausado: number;
    depreciado: number;
  };
  dark: boolean;
  // Admin vê todos os status; usuário comum vê Todos/Produção/Homologação.
  showAllStatuses?: boolean;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const opts = [
    // "Todos" primeiro e como padrão: quem entra no catálogo vê o portfólio
    // inteiro, sem precisar saber o que significa cada status.
    { id: 'todos', l: 'Todos', c: '#78716c', n: counts.todos },
    { id: 'prod', l: 'Produção', c: '#16a34a', n: counts.prod },
    { id: 'homolog', l: 'Homologação', c: '#ca8a04', n: counts.homolog },
    ...(showAllStatuses
      ? [
          { id: 'dev', l: 'Dev', c: '#2563eb', n: counts.dev },
          { id: 'manutencao', l: 'Manutenção', c: '#ea580c', n: counts.manutencao },
          { id: 'pausado', l: 'Pausado', c: '#9a348c', n: counts.pausado },
          { id: 'depreciado', l: 'Depreciado', c: '#78716c', n: counts.depreciado },
        ]
      : []),
  ];
  return (
    <div
      style={{
        /*
          Abaixo de 1024px a pílula deixa de ser de linha única e EMBRULHA. É o
          pior caso medido da tela: as 6 abas do admin somam 993px de conteúdo
          e a faixa tinha 327px a 375px — 666px vazavam para fora (a caixa é
          `overflow: visible`, então vazava de verdade, sem nem virar barra de
          rolagem). Rolagem lateral deliberada resolveria a barra, mas deixaria
          "Manutenção" e "Depreciado" fora do campo de visão de quem não sabe
          que dá para arrastar; em duas ou três linhas as 6 abas aparecem todas.
        */
        display: byViewport(vp, { mobile: 'flex', tablet: 'flex', desktop: 'inline-flex' }),
        flexWrap: byViewport(vp, { mobile: 'wrap', tablet: 'wrap', desktop: 'nowrap' }),
        width: byViewport(vp, { mobile: '100%', tablet: '100%', desktop: undefined }),
        padding: 3,
        background: p.surface2,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = sel === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => setEnv?.(o.id)}
            style={{
              // 40px de alvo de toque no celular (eram 28); no desktop, 28.
              height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 28 }),
              // Padding menor no compacto entra na conta do embrulho: com
              // '0 8px' as 6 abas fecham em 3 linhas tanto a 375px como a
              // 320px — com os '0 14px' originais a 320px sobravam 4 linhas.
              padding: byViewport(vp, { mobile: '0 8px', tablet: '0 12px', desktop: '0 14px' }),
              // No celular as abas crescem para preencher a linha: sem isso a
              // pílula fica com um degrau branco à direita de cada linha.
              flex: byViewport(vp, { mobile: '1 1 auto', tablet: undefined, desktop: undefined }),
              justifyContent: byViewport(vp, {
                mobile: 'center',
                tablet: undefined,
                desktop: undefined,
              }),
              background: active ? p.surface : 'transparent',
              border: 'none',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              color: active ? p.text : p.textMuted,
              fontFamily: fontSans,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              // Trava só no compacto: com a linha embrulhando, o rótulo não pode
              // ser o que quebra (uma aba de duas linhas desalinharia a fileira).
              whiteSpace: byViewport(vp, {
                mobile: 'nowrap',
                tablet: 'nowrap',
                desktop: undefined,
              }),
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: o.c,
              }}
            />
            {o.l}
            <span
              style={{
                ...numeroTabular,
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 999,
                background: active ? p.surface2 : 'transparent',
                color: p.muted,
              }}
            >
              {o.n}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function FavoriteBtn({
  active,
  dark,
  onToggle,
  pending = false,
  size,
}: Readonly<{
  active: boolean;
  dark: boolean;
  /** Recebe o estado DESEJADO: true favorita, false remove dos favoritos. */
  onToggle?: (next: boolean) => void;
  /** Mutation em voo: desabilita para não disparar dois toggles no mesmo card. */
  pending?: boolean;
  /** Sobrescreve o diâmetro; sem isto, 40px no toque e 30px no desktop. */
  size?: number;
}>) {
  const vp = useViewport();
  const p = makePalette(dark);
  // 30px é o desenho denso do desktop, onde o alvo é o cursor. No toque a estrela
  // é a ação que a própria tela anuncia ("Toque na estrela para favoritar"), e
  // 30px fica abaixo do piso de 40px adotado no projeto — daí crescer a pastilha
  // (não um expansor invisível, que já criou rolagem fantasma em outro botão).
  const diameter = size ?? byViewport(vp, { mobile: 40, tablet: 40, desktop: 30 });
  const toggleLabel = active ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
  const label = pending ? 'Salvando favorito…' : toggleLabel;
  // A pastilha é clara nos dois temas; no escuro o `muted` do tema é claro
  // demais sobre ela, então a estrela vazia usa um cinza mais fechado para
  // continuar legível.
  const idleColor = dark ? '#57534e' : p.muted;
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={pending}
      onClick={(e: React.MouseEvent) => {
        // O card inteiro é clicável (abre a aplicação ou navega para o detalhe):
        // o clique na estrela precisa morrer aqui.
        e.stopPropagation();
        e.preventDefault();
        onToggle?.(!active);
      }}
      style={{
        width: diameter,
        height: diameter,
        borderRadius: diameter / 2,
        background: active ? '#fff' : 'rgba(255,255,255,0.85)',
        border: 'none',
        cursor: pending ? 'progress' : 'pointer',
        opacity: pending ? 0.65 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
        color: active ? '#f59e0b' : idleColor,
        backdropFilter: 'blur(4px)',
      }}
    >
      {/* estrela — o glifo acompanha a pastilha (15px na de 30, 20px na de 40
          do celular): fixo em 15 ele ficava perdido no meio do botão maior. */}
      <svg
        width={Math.round(diameter / 2)}
        height={Math.round(diameter / 2)}
        viewBox="0 0 24 24"
        fill={active ? '#f59e0b' : 'none'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
}

export function AppCard({
  app,
  dark,
  accent,
  env,
  onOpen,
  custoUsd,
}: Readonly<{
  app: App;
  dark: boolean;
  accent: string;
  env: 'prod' | 'homolog';
  onOpen?: () => void;
  /**
   * Custo estimado no Railway (USD/mês) — SÓ para administrador, porque o
   * catálogo é a tela de todos os colaboradores e custo é dado financeiro.
   * `undefined` esconde o rótulo (é o caso do colaborador); `null` mostra "—"
   * (admin, mas a aplicação não casou com nenhum projeto).
   */
  custoUsd?: number | null;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Favorito é dado do servidor (por usuário): a estrela lê `app.favorite` e o
  // clique vai para a mutation, que atualiza o cache de forma otimista. Estado
  // local aqui perderia a marcação em qualquer F5.
  const toggleFavorite = useToggleFavorite();
  const favorite = !!app.favorite;
  // Sem `urlHml` o clique cai em PRODUÇÃO, então o card não pode se pintar de
  // homologação: mostramos o selo de produção + o aviso de ambiente ausente.
  const missingHomolog = env === 'homolog' && !app.urlHml;
  const shownEnv: 'prod' | 'homolog' = missingHomolog ? 'prod' : env;
  const isHomolog = shownEnv === 'homolog';

  return (
    // O card inteiro abre a aplicação, mas o <div> externo NÃO carrega
    // `role="button"`: a estrela de favorito é um <button> de verdade e mora
    // dentro dele — botão dentro de botão é HTML inválido. Quem recebe o clique
    // é o <button> transparente sobreposto (logo abaixo).
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        cursor: 'pointer',
        overflow: 'hidden',
        // Ease-out mais longo: o card assenta em vez de "pular". O hover era
        // salto de 5px + anel vermelho sólido + brilho no acento — bruto para
        // uma grade inteira de cards; agora é elevação discreta com sombra
        // neutra e só um sopro do acento na borda.
        transition:
          'border-color 220ms cubic-bezier(0.25, 0.6, 0.25, 1), transform 220ms cubic-bezier(0.25, 0.6, 0.25, 1), box-shadow 220ms cubic-bezier(0.25, 0.6, 0.25, 1)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = `${accent}55`;
        e.currentTarget.style.boxShadow = dark
          ? '0 10px 28px -10px rgba(0,0,0,0.55)'
          : '0 10px 28px -10px rgba(28,25,23,0.18)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = p.border;
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/*
        ABRIR A APLICAÇÃO — um <button> nativo cobrindo o card. Ganhos sobre o
        `role="button"` + `tabIndex` + `onKeyDown` que estavam no <div>: foco,
        Enter e Espaço vêm do elemento (nada de tratar tecla à mão, nada de
        comparar `e.target` com `e.currentTarget` para não roubar o Enter da
        estrela) e o card deixa de anunciar "botão" englobando outro botão.
        Vem PRIMEIRO no DOM de propósito: o Tab alcança "Abrir <app>" antes da
        estrela, que é a ordem de leitura do card.
      */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Abrir ${app.name}`}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      />

      {/* Captura em destaque */}
      <div style={{ position: 'relative' }}>
        <AppScreenshot app={app} dark={dark} env={shownEnv} height={148} />

        {/* Aviso: app listada em homologação sem URL de homologação */}
        {missingHomolog && (
          <div
            title="Esta aplicação não tem ambiente de homologação cadastrado. O acesso abre o sistema de produção."
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              maxWidth: 'calc(100% - 20px)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 9px',
              borderRadius: 4,
              ...rotuloCaixaAlta,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              background: dark ? '#1a1408' : '#fffbeb',
              color: dark ? '#fde68a' : '#78350f',
              border: `1px solid ${dark ? '#78350f' : '#fde68a'}`,
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <IconAlert size={11} /> Sem ambiente de homologação
          </div>
        )}

        {/* Favorito (canto superior direito) — `zIndex` acima do botão-camada,
            senão a sobreposição engoliria o clique na estrela. */}
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
          <FavoriteBtn
            active={favorite}
            dark={dark}
            pending={toggleFavorite.isPending}
            // A tela pede "toque na estrela para favoritar": no celular ela
            // precisa dos 40px de alvo (media 30x30). Acima, 30 como sempre.
            size={byViewport(vp, { mobile: 40, tablet: 30, desktop: 30 })}
            onToggle={(next) => toggleFavorite.mutate({ id: app.id, favorite: next })}
          />
        </div>
      </div>

      {/* Corpo */}
      <div
        style={{
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15.5,
                fontWeight: 600,
                color: p.text,
                letterSpacing: '-0.012em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginBottom: 4,
              }}
            >
              {app.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <VisibilityBadge visibility={app.visibility} dark={dark} />
              <span style={{ color: p.muted, fontSize: 11, fontFamily: fontSans }}>·</span>
              <span
                style={{
                  fontSize: 11,
                  color: p.muted,
                  fontFamily: fontSans,
                }}
              >
                {app.team}
              </span>
            </div>
          </div>
          <span style={{ color: p.muted, display: 'flex', marginTop: 2 }}>
            <IconExternal size={14} />
          </span>
        </div>

        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.55,
            color: p.textMuted,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 38,
          }}
        >
          {app.description}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 10,
            borderTop: `1px solid ${p.border}`,
            ...numeroTabular,
            fontSize: 11,
            color: p.muted,
          }}
        >
          <span
            style={{
              ...rotuloCaixaAlta,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: isHomolog ? '#ca8a04' : '#16a34a',
              fontWeight: 700,
              // line box = fonte → bolinha centrada nas maiúsculas em qualquer
              // fonte/escala de tela (ver comentário em app-visuals.tsx).
              lineHeight: 1,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: isHomolog ? '#ca8a04' : '#16a34a',
                // −0.5px óptico (ver comentário em app-visuals.tsx).
                transform: 'translateY(-0.5px)',
              }}
            />
            {isHomolog ? 'HOMOLOGAÇÃO' : 'PRODUÇÃO'}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {/* Custo do Railway · discreto, na mesma linha do "atual." e apenas
                para admin (o rótulo nem é renderizado para colaborador). "—"
                quando não há correspondência: "US$ 0,00" mentiria. */}
            {custoUsd !== undefined && (
              <>
                <span
                  title={
                    custoUsd === null
                      ? 'Sem custo estimado no Railway: aplicação sem repositório cadastrado ou sem projeto correspondente.'
                      : 'Estimativa de custo do mês no Railway (não é a fatura) · visível apenas para administradores'
                  }
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {custoUsd === null ? '—' : `${formatUsdBR(custoUsd)}/mês`}
                </span>
                <span style={{ opacity: 0.45 }}>·</span>
              </>
            )}
            <span>atual. {formatDateShortBR(app.lastUpdate)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterGroupDef {
  id: string;
  label: string;
  options: FilterOption[];
  selected: string[];
  toggle: (id: string) => void;
}

// Chip de filtro ativo (removível) — mesma família visual do FilterChip, mas
// pintado com o acento para se ler como "filtro aplicado".
function ActiveFilterChip({
  label,
  dark,
  accent,
  onRemove,
}: Readonly<{
  label: string;
  dark: boolean;
  accent: string;
  onRemove: () => void;
}>) {
  const vp = useViewport();
  return (
    <span
      style={{
        // Chip um pouco mais alto no celular só para o "x" caber maior — 26px
        // de alvo em vez de 18px. Não chega aos 40px de um botão de verdade, e
        // não deve: é a ação secundária DENTRO do chip, e um alvo de 40 faria a
        // linha de filtros ativos ocupar o dobro da altura do conteúdo.
        height: byViewport(vp, { mobile: 32, tablet: 26, desktop: 26 }),
        padding: '0 5px 0 10px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
        fontFamily: fontSans,
        color: accent,
        background: dark ? `${accent}1f` : `${accent}0f`,
        border: `1px solid ${accent}66`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      <button
        type="button"
        title={`Remover filtro ${label}`}
        aria-label={`Remover filtro ${label}`}
        onClick={onRemove}
        style={{
          width: byViewport(vp, { mobile: 26, tablet: 18, desktop: 18 }),
          height: byViewport(vp, { mobile: 26, tablet: 18, desktop: 18 }),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          border: 'none',
          background: 'transparent',
          color: accent,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <IconX size={11} />
      </button>
    </span>
  );
}

// Barra de filtros expansível (em vez de popover): o painel precisa acomodar
// três grupos de chips com contadores e a lista de times cresce com a empresa —
// inline não tem risco de corte no scroll, acompanha o resize e reaproveita o
// mesmo FilterChip do inventário.
function CatalogFilterPanel({
  groups,
  dark,
  accent,
  shown,
  total,
  activeFilters,
  onClear,
}: Readonly<{
  groups: FilterGroupDef[];
  dark: boolean;
  accent: string;
  shown: number;
  total: number;
  activeFilters: number;
  onClear: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        flexShrink: 0,
        background: p.surface,
        borderBottom: `1px solid ${p.border}`,
        padding: byViewport(vp, { mobile: '12px 14px', tablet: '14px 16px', desktop: '16px 24px' }),
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {groups.map((g) => (
        <div
          key={g.id}
          style={{
            display: 'flex',
            // No celular o rótulo do grupo vai para cima dos chips: os 140px
            // fixos da coluna de rótulo deixavam 173px de 327 para os chips, e
            // "Automação/Integração" quebrava sozinho em três linhas.
            flexDirection: byViewport(vp, { mobile: 'column', tablet: 'row', desktop: 'row' }),
            alignItems: 'flex-start',
            gap: byViewport(vp, { mobile: 6, tablet: 14, desktop: 14 }),
          }}
        >
          <div
            style={{
              width: byViewport<string | number>(vp, { mobile: 'auto', tablet: 140, desktop: 140 }),
              flexShrink: 0,
              paddingTop: byViewport(vp, { mobile: 0, tablet: 8, desktop: 8 }),
              ...rotuloCaixaAlta,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: p.muted,
            }}
          >
            {g.label}
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              // Empilhado (celular) o eixo principal é vertical, então `flex: 1`
              // já não estica na largura: sem isto a faixa de chips ficaria com
              // a largura do maior chip em vez da linha inteira.
              width: byViewport(vp, { mobile: '100%', tablet: undefined, desktop: undefined }),
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              maxHeight: 108,
              overflowY: 'auto',
            }}
          >
            {g.options.length === 0 ? (
              <span style={{ fontSize: 12, color: p.muted, paddingTop: 7 }}>
                Nada para filtrar aqui.
              </span>
            ) : (
              g.options.map((o) => (
                <FilterChip
                  key={o.id}
                  label={o.label}
                  count={o.count}
                  active={g.selected.includes(o.id)}
                  dark={dark}
                  accent={accent}
                  onClick={() => g.toggle(o.id)}
                />
              ))
            )}
          </div>
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderTop: `1px solid ${p.border}`,
          paddingTop: 12,
        }}
      >
        <span style={{ ...numeroTabular, fontSize: 11.5, color: p.muted }}>
          {shown} de {total} aplicações
        </span>
        <div style={{ flex: 1 }} />
        {activeFilters > 0 && (
          <ButtonGhost dark={dark} onClick={onClear}>
            <IconX size={12} /> Limpar filtros
          </ButtonGhost>
        )}
      </div>
    </div>
  );
}

// Faixa de aviso da aba de homologação: diz onde o usuário está, que os dados
// podem ser de teste, e oferece a volta para produção.
function HomologBanner({ dark, onExit }: Readonly<{ dark: boolean; onExit: () => void }>) {
  const vp = useViewport();
  return (
    <div
      style={{
        background: dark ? '#1a1408' : '#fffbeb',
        borderBottom: `1px solid ${dark ? '#78350f' : '#fde68a'}`,
        padding: byViewport(vp, { mobile: '12px 14px', tablet: '12px 16px', desktop: '12px 24px' }),
        display: 'flex',
        alignItems: 'center',
        // No celular o botão "← Todos os apps" desce para a própria linha: ao
        // lado das duas frases do aviso não sobra largura para ele.
        flexWrap: byViewport(vp, { mobile: 'wrap', tablet: 'nowrap', desktop: 'nowrap' }),
        gap: byViewport(vp, { mobile: 12, tablet: 16, desktop: 16 }),
        position: 'relative',
      }}
    >
      {/* Faixa de destaque à esquerda */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 3,
          background: '#ca8a04',
        }}
      />

      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: dark ? '#ca8a0422' : '#fef3c7',
          color: '#ca8a04',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <IconAlert size={16} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              ...rotuloCaixaAlta,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 3,
              background: '#ca8a04',
              color: '#fff',
            }}
          >
            Homologação
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: dark ? '#fde68a' : '#78350f',
            }}
          >
            Ambiente de testes · versões em validação
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: dark ? '#d6d3d1' : '#78716c',
            fontFamily: fontSans,
          }}
        >
          Dados podem ser de teste · funcionalidades podem mudar a qualquer momento
        </div>
      </div>

      <button
        type="button"
        onClick={onExit}
        style={{
          height: byViewport(vp, { mobile: 40, tablet: 32, desktop: 32 }),
          // Sozinho na linha, o botão ocupa a largura toda — no celular, alvo
          // que se lê como o próximo passo, e não um retalho de 120px.
          width: byViewport(vp, { mobile: '100%', tablet: undefined, desktop: undefined }),
          justifyContent: byViewport(vp, {
            mobile: 'center',
            tablet: undefined,
            desktop: undefined,
          }),
          padding: '0 14px',
          background: 'transparent',
          color: dark ? '#fde68a' : '#78350f',
          border: `1px solid ${dark ? '#78350f' : '#fde68a'}`,
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontFamily: fontSans,
        }}
      >
        ← Todos os apps
      </button>
    </div>
  );
}

export function ScreenCatalog({
  dark,
  accent = NOC_RED,
  apps,
  role = 'collab',
  updatedAt,
}: Readonly<
  ScreenProps & {
    apps: App[];
    role?: 'admin' | 'collab';
    /**
     * Carimbo do último fetch da lista (ex.: `dataUpdatedAt` do React Query).
     * Opcional: sem ele o rótulo de atualização simplesmente não aparece.
     */
    updatedAt?: number | Date;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Aba e filtros vêm do store PERSISTIDO (localStorage): abrir o detalhe de
  // um app e voltar — ou navegar para qualquer outra página — mantém a seleção
  // que o usuário montou. Multisseleção dentro do grupo (OU), interseção entre
  // grupos (E). A busca é efêmera de propósito: pergunta, não preferência.
  const storedView = useCatalogPrefs((s) => s.view);
  const typeSel = useCatalogPrefs((s) => s.typeSel);
  const critSel = useCatalogPrefs((s) => s.critSel);
  const teamSel = useCatalogPrefs((s) => s.teamSel);
  const setView = useCatalogPrefs((s) => s.setView);
  const toggleType = useCatalogPrefs((s) => s.toggleType);
  const toggleCrit = useCatalogPrefs((s) => s.toggleCrit);
  const toggleTeam = useCatalogPrefs((s) => s.toggleTeam);
  const clearFilters = useCatalogPrefs((s) => s.clearFilters);

  // Colaborador com aba de admin herdada (era admin e deixou de ser, ou
  // localStorage editado) cai em "Todos" — na leitura, sem reescrever o store.
  const view: CatalogView =
    role !== 'admin' && ADMIN_ONLY_VIEWS.includes(storedView) ? 'todos' : storedView;

  // Só a aba de homologação apresenta os cards como homologação; "Todos" e os
  // status de admin mostram produção — o clique nunca finge ambiente de teste.
  const cardEnv: 'prod' | 'homolog' = view === 'homolog' ? 'homolog' : 'prod';

  // Admin: clicar no card abre o detalhe (visão completa). Colaborador: abre a
  // própria aplicação em nova aba — na aba de homologação, a URL de
  // homologação (resolveAppUrl), nunca a de produção sem aviso.
  const openApp = (a: App) => {
    if (role === 'admin') navigate(`/app/${a.slug}`);
    else {
      // App sem NENHUMA URL cadastrada (situação dev, por exemplo): não há o
      // que abrir — clique vira no-op em vez de aba em branco.
      const alvo = resolveAppUrl(a, cardEnv);
      if (alvo) window.open(alvo, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Custo do Railway nos cards · SÓ admin. Decisão de produto: custo é dado
   * financeiro e este catálogo é visto por todos os colaboradores. A consulta
   * também fica desligada para colaborador (`enabled`), senão cada abertura do
   * catálogo levaria um 403 da rota admin-only.
   */
  const admin = role === 'admin';
  const { data: costs } = useRailwayCosts(admin);
  const custoPorSlug = costsByRepoSlug(costs?.custos);
  // `undefined` (colaborador) esconde o rótulo no card; `null` mostra "—".
  const custoDeApp = (a: App): number | null | undefined => {
    if (!admin) return undefined;
    const slug = repoSlug(a.repo);
    if (!slug) return null;
    return custoPorSlug.get(slug)?.estimadoMesUsd ?? null;
  };

  const activeFilters = typeSel.length + critSel.length + teamSel.length;

  const visibleApps = apps.filter(
    // Interno é infraestrutura: colaborador nunca vê (a API nem manda a linha),
    // mas ADMIN vê no catálogo também — a mesma regra da página de Favoritos e
    // o que o aviso do cadastro promete ("somente administradores conseguem
    // visualizar"). Este filtro escondia de todo mundo, admin incluso. Os tipos
    // de administração (RPA, integração…) continuam fora para todos: o lugar
    // deles é a tela Apps internos.
    (a) => (role === 'admin' || a.visibility !== 'interno') && !ADMIN_ONLY_TYPES.has(a.type),
  );

  // Base da aba ativa: "Todos" lista tudo; Produção/Homologação e os status de
  // admin mantêm as regras de sempre.
  const envApps = appsForView(visibleApps, view);

  // Busca insensível a acento: "conciliacao" encontra "Conciliação" — mesma
  // régua de normalização do "Meus apps" (lib/format.ts).
  const q = normalizeSearchText(query);
  const matchesQuery = (a: App) =>
    !q || [a.name, a.team, a.owner, a.description].some((f) => normalizeSearchText(f).includes(q));
  const matchesType = (a: App) => typeSel.length === 0 || typeSel.includes(a.type);
  const matchesCrit = (a: App) => critSel.length === 0 || critSel.includes(a.criticality);
  const matchesTeam = (a: App) => teamSel.length === 0 || teamSel.includes(a.team);

  const shown = envApps.filter(
    (a) => matchesQuery(a) && matchesType(a) && matchesCrit(a) && matchesTeam(a),
  );
  // FAVORITO SOBE: com a tela separada de favoritos fora do ar, o favorito virou
  // ordenação DENTRO do catálogo. A partição em dois blocos é uma ordenação
  // ESTÁVEL por `favorite` — cada `filter` percorre `shown` na ordem original,
  // então a ordem relativa dentro de cada bloco é exatamente a que a aba, os
  // chips e a busca produziram. Convive com tudo: filtramos primeiro, ordenamos
  // depois. O clique na estrela reordena na hora porque `useToggleFavorite` faz
  // optimistic update no cache ['applications'].
  const favs = shown.filter((a) => a.favorite);
  const others = shown.filter((a) => !a.favorite);

  // Contadores dos chips: contam sobre a base do ambiente + busca + os OUTROS
  // grupos (a faceta não conta a si mesma), então o número diz quantas apps
  // sobram se você ligar aquele chip.
  const typeBase = envApps.filter((a) => matchesQuery(a) && matchesCrit(a) && matchesTeam(a));
  const critBase = envApps.filter((a) => matchesQuery(a) && matchesType(a) && matchesTeam(a));
  const teamBase = envApps.filter((a) => matchesQuery(a) && matchesType(a) && matchesCrit(a));

  const groups: FilterGroupDef[] = [
    {
      id: 'type',
      label: 'Tipo de aplicação',
      options: Object.keys(APP_TYPES)
        .filter((t) => envApps.some((a) => a.type === t))
        .map((t) => ({
          id: t,
          label: APP_TYPES[t].label,
          count: typeBase.filter((a) => a.type === t).length,
        })),
      selected: typeSel,
      toggle: toggleType,
    },
    {
      id: 'criticality',
      label: 'Criticidade',
      options: ['alta', 'media', 'baixa']
        .filter((c) => envApps.some((a) => a.criticality === c))
        .map((c) => ({
          id: c,
          label: CRITICALITY_LABELS[c] ?? c,
          count: critBase.filter((a) => a.criticality === c).length,
        })),
      selected: critSel,
      toggle: toggleCrit,
    },
    {
      id: 'team',
      label: 'Time responsável',
      options: [...new Set(envApps.map((a) => a.team))]
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
        .map((t) => ({
          id: t,
          label: t,
          count: teamBase.filter((a) => a.team === t).length,
        })),
      selected: teamSel,
      toggle: toggleTeam,
    },
  ];

  const activeChips = [
    ...typeSel.map((t) => ({
      key: `type:${t}`,
      label: `Tipo: ${APP_TYPES[t]?.label ?? t}`,
      remove: () => toggleType(t),
    })),
    ...critSel.map((c) => ({
      key: `criticality:${c}`,
      label: `Criticidade: ${CRITICALITY_LABELS[c] ?? c}`,
      remove: () => toggleCrit(c),
    })),
    ...teamSel.map((t) => ({
      key: `team:${t}`,
      label: `Time: ${t}`,
      remove: () => toggleTeam(t),
    })),
  ];

  const counts = {
    todos: visibleApps.length,
    prod: visibleApps.filter(isOnProdTab).length,
    homolog: visibleApps.filter(isOnHomologTab).length,
    dev: visibleApps.filter((a) => a.status === 'dev').length,
    manutencao: visibleApps.filter((a) => a.status === 'manutencao').length,
    pausado: visibleApps.filter((a) => a.status === 'pausado').length,
    depreciado: visibleApps.filter((a) => a.status === 'depreciado').length,
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="catalog" dark={dark} accent={accent} role={role} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <CatalogTopbar
          dark={dark}
          accent={accent}
          query={query}
          setQuery={setQuery}
          filtersOpen={showFilters}
          onToggleFilters={() => setShowFilters((v) => !v)}
          activeFilters={activeFilters}
        />

        {/* Barra de filtros (expansível pelo botão "Filtros") */}
        {showFilters && (
          <CatalogFilterPanel
            groups={groups}
            dark={dark}
            accent={accent}
            shown={shown.length}
            total={envApps.length}
            activeFilters={activeFilters}
            onClear={clearFilters}
          />
        )}

        {/* Banner discreto de homologação — só na aba de homologação */}
        {view === 'homolog' && <HomologBanner dark={dark} onExit={() => setView('todos')} />}

        <div
          style={{
            flex: 1,
            // 24px de cada lado custam 48 dos 375 da tela — 13% da largura só de
            // margem. Com 12 o card do catálogo ganha 24px de conteúdo.
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {/* Controle de ambiente + resumo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              // Embrulha em QUALQUER largura (era só no compacto): no desktop de
              // notebook (~1500-1900px CSS, ex.: FHD com Windows a 125%) as 6
              // abas do admin + resumo + "Atualizado" não cabem numa linha e o
              // nowrap espremia os textos até quebrarem NO MEIO ("12" /
              // "aplicações" / "· 8" / "favoritos", medido). Com o embrulho no
              // container e os textos travados abaixo, o wrap acontece ENTRE
              // blocos: abas na primeira linha, meta inteiro na segunda.
              // (No compacto, a 768px o resumo começava em x=828 e o "Atualizado
              // agora mesmo" em x=932, ambos fora da tela — mesmo remédio.)
              flexWrap: 'wrap',
              gap: byViewport(vp, { mobile: 10, tablet: 12, desktop: 16 }),
              marginBottom: byViewport(vp, { mobile: 16, tablet: 18, desktop: 22 }),
            }}
          >
            <EnvSegmented
              sel={view}
              setEnv={(id: string) => setView(id as CatalogView)}
              counts={counts}
              dark={dark}
              showAllStatuses={role === 'admin'}
            />
            {/* Meta (resumo + frescor) é UM bloco flex: dividindo a linha com as
                abas, cresce e empurra o "Atualizado" para a borda direita; sem
                caber, desce inteiro e ocupa a linha de baixo com o mesmo
                alinhamento (resumo à esquerda, frescor à direita). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: '1 1 auto',
                // Só num celular muito estreito os dois textos se separam — e
                // mesmo aí a quebra é entre eles, nunca dentro de um.
                flexWrap: 'wrap',
                gap: byViewport(vp, { mobile: 10, tablet: 12, desktop: 16 }),
              }}
            >
              <span
                style={{
                  ...numeroTabular,
                  fontSize: 12,
                  color: p.muted,
                  // O texto nunca quebra no meio; quem cede é o wrap dos blocos.
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {activeFilters > 0 || q
                  ? `${shown.length} de ${envApps.length} aplicações`
                  : `${shown.length} aplicações`}{' '}
                · {favs.length} favorito{favs.length === 1 ? '' : 's'}
              </span>
              {updatedAt != null && (
                <span
                  style={{
                    ...numeroTabular,
                    fontSize: 11,
                    color: p.muted,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    // Substitui o antigo spacer <div flex:1>: encosta na direita
                    // tanto na mesma linha do resumo quanto sozinho na de baixo.
                    marginLeft: 'auto',
                  }}
                >
                  <IconRefresh size={12} /> Atualizado {formatFreshness(updatedAt)}
                </span>
              )}
            </div>
          </div>

          {/* Filtros ativos (removíveis) */}
          {activeChips.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 20,
              }}
            >
              <span
                style={{
                  ...rotuloCaixaAlta,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: p.muted,
                  marginRight: 2,
                }}
              >
                Filtros
              </span>
              {activeChips.map((c) => (
                <ActiveFilterChip
                  key={c.key}
                  label={c.label}
                  dark={dark}
                  accent={accent}
                  onRemove={c.remove}
                />
              ))}
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  // Alvo de 40px no celular; o texto sublinhado continua com a
                  // mesma altura visual, é a área de toque que cresce.
                  height: byViewport(vp, { mobile: 40, tablet: 26, desktop: 26 }),
                  padding: '0 10px',
                  background: 'transparent',
                  border: 'none',
                  color: p.muted,
                  fontSize: 11.5,
                  fontWeight: 600,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                Limpar filtros
              </button>
            </div>
          )}

          {/* Favoritos */}
          {favs.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  flexWrap: byViewport(vp, { mobile: 'wrap', tablet: 'nowrap', desktop: 'nowrap' }),
                  gap: byViewport(vp, { mobile: 8, tablet: 0, desktop: 0 }),
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#f59e0b"
                    stroke="#f59e0b"
                    strokeWidth="2"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: p.text,
                        letterSpacing: '-0.012em',
                      }}
                    >
                      Favoritos
                    </div>
                    <div style={{ fontSize: 12, color: p.muted, marginTop: 2 }}>
                      Aplicações que você marcou com a estrela · {favs.length}{' '}
                      {favs.length === 1 ? 'item' : 'itens'}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: appGridColumns(vp),
                  gap: 14,
                  marginBottom: 32,
                }}
              >
                {favs.map((a) => (
                  <AppCard
                    key={a.id}
                    app={a}
                    dark={dark}
                    accent={accent}
                    env={cardEnv}
                    onOpen={() => openApp(a)}
                    custoUsd={custoDeApp(a)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Todas as aplicações */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: p.text,
                  letterSpacing: '-0.012em',
                }}
              >
                Todas as aplicações
              </div>
              <div style={{ fontSize: 12, color: p.muted, marginTop: 2 }}>
                Toque na estrela para favoritar
              </div>
            </div>
          </div>

          {others.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: appGridColumns(vp),
                gap: 14,
              }}
            >
              {others.map((a) => (
                <AppCard
                  key={a.id}
                  app={a}
                  dark={dark}
                  accent={accent}
                  env={cardEnv}
                  onOpen={() => openApp(a)}
                  custoUsd={custoDeApp(a)}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                // 32px de padding em caixa de 296px (320 menos a margem) sobram
                // 232 para a frase: no celular ela cabia em 4 linhas.
                padding: byViewport(vp, { mobile: 18, tablet: 24, desktop: 32 }),
                textAlign: 'center',
                border: `1px dashed ${p.border}`,
                borderRadius: 10,
                color: p.muted,
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span>
                {shown.length === 0
                  ? 'Nenhuma aplicação corresponde à busca ou aos filtros.'
                  : 'Nenhuma outra aplicação além das favoritas.'}
              </span>
              {activeFilters > 0 && (
                <ButtonGhost dark={dark} onClick={clearFilters}>
                  <IconX size={12} /> Limpar filtros
                </ButtonGhost>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
